import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Flex } from 'antd';

import { RequestList } from '@Components/RequestList';

import {
  useAppDispatch,
  useIsLoadedConent,
  useRequestRefetcher,
} from '@Hooks/index';

import { SortConfig } from '@Features/types/SortConfig';

import { TABLE_PAGE_DEFAULT } from '@Features/constants';
import { eventMessageActions } from '@Store/slices/eventMessageSlice';
import { filterOptionsActions } from '@Store/slices/filterOptionsSlice';
import { MSkeleton } from '@Components/Base';
import { AttendanceProfileResponse } from '@Types/api/profile/AttendanceProfileList';
import { RequestListSearchResponse } from '@Types/api/requestList';

import { mapColumns, attendanceProfileDataSourceMap } from './list';
import styles from './latest-requests.module.css';
import OwnAction from './components/OwnAction';

const isRequestFormPage = false;

const LatestRequests: React.FC = () => {
  const isRequestFormPagePrev = useRef(isRequestFormPage);
  const dispatch = useAppDispatch();
  const [sorting, setSorting] = useState<SortConfig | null>(null);

  useEffect(() => {
    isRequestFormPagePrev.current = isRequestFormPage;
  }, [isRequestFormPage]);

  useEffect(() => {
    dispatch(filterOptionsActions.setFilterOptions(null));
  }, []);

  const clearParams = (): void => {
    setSorting(null);
  };

  const {
    currentPage,
    fetchRequest,
    isSuccess,
    handleLoadMore,
    onChangeCurrentPage,
    listRequests,
    requestsDataList,
  } = useRequestRefetcher({
    clearParams,
    filters: undefined,
    isRequestFormPage,
    isRequestFormPagePrev,
    requestType: 'AttendanceProfile',
    searchValue: undefined,
    sorting,
  });

  const isContentLoaded = useIsLoadedConent(isSuccess);

  const dataSource = useMemo(
    () =>
      attendanceProfileDataSourceMap(
        (listRequests as RequestListSearchResponse<AttendanceProfileResponse>)
          ?.content
      ),
    [listRequests]
  );

  const handleChangePage = useCallback((page: number): void => {
    onChangeCurrentPage(page);
    dispatch(eventMessageActions.sendEventType('REFETCH'));
  }, []);

  const handleSort = useCallback(
    (sortConfig: SortConfig | null) => {
      fetchRequest(TABLE_PAGE_DEFAULT, sortConfig);

      onChangeCurrentPage(TABLE_PAGE_DEFAULT);
      setSorting(sortConfig);
    },
    [fetchRequest]
  );

  return (
    <div className={styles.wrapper}>
      <Flex className={styles.header}>
        <div className={styles.title}>Attendance</div>
      </Flex>
      {isContentLoaded ? (
        <RequestList
          onLoadMore={handleLoadMore}
          onChangePage={handleChangePage}
          currentPage={currentPage}
          onSort={handleSort}
          listRequests={
            listRequests as RequestListSearchResponse<AttendanceProfileResponse>
          }
          requestsDataList={
            requestsDataList as Array<AttendanceProfileResponse>
          }
          mapColumns={mapColumns}
          dataSource={dataSource}
          requestType="AttendanceProfile"
          ownActions={<OwnAction />}
        />
      ) : (
        <MSkeleton />
      )}
    </div>
  );
};

export default LatestRequests;
